import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Youtube, Linkedin, Globe } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { useEffect, useMemo, useState } from 'react';
import { getStorefrontConfig } from '../../utils/storefrontConfig';
import {
  resolveFooterContent,
  pickFooterContent,
  formatCopyright,
  type FooterSocial,
} from '../../utils/footerContent';
import { NewsletterFooter } from './NewsletterFooter';
import { Editable } from '../storefront/inline/Editable';

const FOOTER_SOCIAL_ICON: Record<string, React.ReactNode> = {
  instagram: <Instagram size={16} />,
  facebook: <Facebook size={16} />,
  twitter: <Twitter size={16} />,
  youtube: <Youtube size={16} />,
  linkedin: <Linkedin size={16} />,
  tiktok: <Globe size={16} />,
  pinterest: <Globe size={16} />,
  custom: <Globe size={16} />,
};

export const footerSocialIconFor = (s: FooterSocial): React.ReactNode =>
  FOOTER_SOCIAL_ICON[s.platform] || FOOTER_SOCIAL_ICON.custom;

type FooterMenuLink = {
  label: string;
  url: string;
};

type FooterMenuSection = {
  title: string;
  links: FooterMenuLink[];
};

const DEFAULT_SEED_FOOTER_MENU: FooterMenuSection[] = [
  {
    title: 'SHOP',
    links: [
      { label: 'Sendy', url: '/products?collection=sendy' },
      { label: 'Shop All', url: '/products' },
    ],
  },
  {
    title: 'ABOUT',
    links: [{ label: 'About Syntara', url: '/about-syntara' }],
  },
  {
    title: 'SOCIALS',
    links: [
      { label: 'Facebook', url: 'https://facebook.com' },
      { label: 'Instagram', url: 'https://instagram.com' },
    ],
  },
  {
    title: 'HELP',
    links: [{ label: 'Contact Us', url: '/contact' }],
  },
];

const normalizeSeedFooterMenu = (menu: any): FooterMenuSection[] => {
  if (!Array.isArray(menu) || menu.length === 0) {
    return DEFAULT_SEED_FOOTER_MENU.map((section) => ({
      ...section,
      links: section.links.map((link) => ({ ...link })),
    }));
  }

  const normalized = menu
    .map((section: any, sectionIdx: number) => {
      const fallbackSection = DEFAULT_SEED_FOOTER_MENU[sectionIdx] || { title: `COLUMN ${sectionIdx + 1}`, links: [{ label: 'Link', url: '#' }] };
      const incomingLinks = Array.isArray(section?.links) && section.links.length > 0
        ? section.links
        : fallbackSection.links;

      return {
        title: String(section?.title ?? fallbackSection.title),
        links: incomingLinks.map((link: any, linkIdx: number) => {
          const fallbackLink = fallbackSection.links[linkIdx] || { label: `Link ${linkIdx + 1}`, url: '#' };
          return {
            label: String(link?.label ?? fallbackLink.label),
            url: String(link?.url ?? fallbackLink.url),
          };
        }),
      };
    })
    .filter((section: FooterMenuSection) => section.links.length > 0);

  return normalized.length > 0 ? normalized : DEFAULT_SEED_FOOTER_MENU.map((section) => ({
    ...section,
    links: section.links.map((link) => ({ ...link })),
  }));
};

const Footer = () => {
  const { tenant } = useTenant();
  const location = useLocation();
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [storefrontConfig, setStorefrontConfig] = useState<any>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Detect funnel slug from URL - check for /f/:slug pattern first
        const pathname = location.pathname;
        const funnelMatch = pathname.match(/^\/f\/([^\/]+)/); // Match /f/challenge, /f/webinar, etc.
        const funnelSlug = funnelMatch ? funnelMatch[1] : undefined;

        const config = await getStorefrontConfig(undefined, funnelSlug);
        setStorefrontConfig(config);
        if (config?.customLogo) {
          setCustomLogo(config.customLogo);
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
      }
    };
    fetchConfig();
  }, [tenant, location.pathname]);

  // Live builder preview: admin postMessages new config → previewBridge
  // dispatches divi:preview-config. Update local state so footer edits
  // reflect without iframe reload.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'object') {
        setStorefrontConfig(detail);
        if (detail.customLogo) setCustomLogo(detail.customLogo);
      }
    };
    window.addEventListener('divi:preview-config', handler as EventListener);
    return () => window.removeEventListener('divi:preview-config', handler as EventListener);
  }, []);

  if (!tenant) return null;

  const currentYear = new Date().getFullYear();
  
  // Get primary color for logo circle
  const getCSSVariable = (varName: string): string | null => {
    if (typeof window === 'undefined') return null;
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return value || null;
  };

  const primaryColor =
    getCSSVariable('--primary-color') ||
    getCSSVariable('--color-primary') ||
    tenant.branding?.primaryColor ||
    '#4F7DFF';

  // Match builder preview logic EXACTLY:
  // Builder preview checks: (colors as any).footerBg || config?.customColors?.footerBg || '#222222'
  // The colors object comes from pageSettings.colors (pagesConfig[activePage].colors)
  // Even though footerBg isn't in the type, it might be in the actual data
  // So we check page-specific colors first, then customColors, then default
  
  // Determine current page from URL (matching page config keys)
  const pathname = location.pathname.toLowerCase();
  let currentPage = 'home';
  if (pathname.startsWith('/products/')) {
    currentPage = 'product-details';
  } else if (pathname.startsWith('/products')) {
    currentPage = 'products';
  } else if (pathname.startsWith('/cart')) {
    currentPage = 'cart';
  } else if (pathname.startsWith('/checkout')) {
    currentPage = 'checkout';
  } else if (pathname.startsWith('/order-success') || pathname.startsWith('/order-confirmation')) {
    currentPage = 'order-success';
  }
  
  // Get page-specific colors (like builder preview's pageSettings.colors)
  const pageColors = ((storefrontConfig?.pagesConfig?.[currentPage]?.colors as any)
    || (currentPage === 'product-details' ? (storefrontConfig?.pagesConfig?.['product-detail']?.colors as any) : undefined)
    || {});
  const pageLayoutVariant =
    (storefrontConfig?.pagesConfig?.[currentPage]?.layoutVariant as string)
    || (currentPage === 'product-details' ? (storefrontConfig?.pagesConfig?.['product-detail']?.layoutVariant as string) : undefined)
    || 'default';
  const pageFooterVariant =
    (storefrontConfig?.pagesConfig?.[currentPage]?.footerVariant as string)
    || (currentPage === 'product-details' ? (storefrontConfig?.pagesConfig?.['product-detail']?.footerVariant as string) : undefined)
    || 'default';
  const pageFooterMenu =
    (storefrontConfig?.pagesConfig?.[currentPage]?.footerMenu as any)
    || (currentPage === 'product-details' ? (storefrontConfig?.pagesConfig?.['product-detail']?.footerMenu as any) : undefined);
  const customColors = storefrontConfig?.customColors || {};
  
  // Match builder preview: check pageColors.footerBg first (even though not in type), then customColors.footerBg
  const footerBgColor = (pageColors.footerBg as string) || customColors.footerBg || '#222222';
  const footerTextColor = (pageColors.footerText as string) || customColors.footerText || '#FFFFFF';

  // Use custom logo from storefront config if available, otherwise fallback to tenant branding logo
  const logo = customLogo || tenant.branding?.logo;
  const companyName =
    storefrontConfig?.storeName ||
    tenant?.branding?.companyName ||
    tenant?.name ||
    storefrontConfig?.tenantName ||
    'Store';
  const logoInitial = companyName.charAt(0).toUpperCase();
  // Footer variant comes ONLY from explicit pageFooterVariant. No cross-derivation
  // from layoutVariant - admin's footer Default pick stays default even on
  // sugar/seed header.
  const isSeedFooterVariant = pageFooterVariant === 'seed';
  const isSugarFooterVariant = pageFooterVariant === 'sugar';
  const isNewsletterFooterVariant = pageFooterVariant === 'newsletter';
  const seedFooterMenu = normalizeSeedFooterMenu(pageFooterMenu);

  // Resolve editable footer content (applies to all layouts via this shared Footer)
  const footerContent = useMemo(() => resolveFooterContent(
    pickFooterContent(storefrontConfig),
    {
      newsletter: { enabled: false },
      brandBio: '',
      menuColumns: seedFooterMenu.map((s) => ({
        title: s.title,
        links: s.links.map((l) => ({ label: l.label, url: l.url })),
      })),
      socials: [],
      bottomLinks: [
        { label: 'Privacy Policy', url: '/privacy' },
        { label: 'Terms & Conditions', url: '/terms' },
      ],
      copyright: '© Copyright {year}. All rights reserved.',
    },
  ), [storefrontConfig, seedFooterMenu]);

  if (isSugarFooterVariant) {
    const sgFooterBg = (pageColors.footerBg as string) || customColors.footerBg || customColors.text || '#7C2A60';
    const sgFooterInk = (pageColors.footerText as string) || customColors.footerText || customColors.background || '#FFF3CE';
    const sgLime = customColors.primary || primaryColor;
    const sgSecondary = customColors.secondary || sgLime;
    // Defaults match BaseTemplate sugar footer so home page (BaseTemplate)
    // and inner pages (Layout shared Footer) line up when user hasn't edited
    // Footer Content yet.
    const dynamicPages: any[] = Array.isArray(storefrontConfig?.pages) ? storefrontConfig.pages : [];
    const sugarDefaultColumns = [
      {
        title: 'Shop',
        links: [
          { label: companyName.toLowerCase(), url: '/products' },
          { label: 'cart', url: '/cart' },
        ],
      },
      {
        title: 'Contact',
        links: dynamicPages
          .filter((p) => p?.slug && p?.isPublished !== false)
          .slice(0, 3)
          .map((p) => ({ label: String(p.title || p.name || p.slug), url: `/${p.slug}` })),
      },
      {
        title: 'Whatelse',
        links: [
          { label: 'Instagram', url: '#' },
          { label: 'TikTok', url: '#' },
          { label: 'YouTube', url: '#' },
        ],
      },
    ];
    // Read raw user-edited footerContent (resolveFooterContent fills menuColumns
    // with seedFooterMenu defaults when user hasn't edited, which would mask
    // sugarDefaultColumns). Only use raw.menuColumns if explicitly saved.
    const rawFooterContent = pickFooterContent(storefrontConfig);
    const userMenuColumns = Array.isArray(rawFooterContent?.menuColumns)
      && rawFooterContent.menuColumns.length > 0
      ? rawFooterContent.menuColumns
      : null;
    const sugarColumns = userMenuColumns || sugarDefaultColumns;
    return (
      <footer
        className="mt-auto"
        style={{ background: sgFooterBg, color: sgFooterInk, padding: '80px 0 56px' }}
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 48, alignItems: 'start' }}>
            <div className="md:col-span-1">
              {footerContent.brandLogoUrl ? (
                <img
                  src={footerContent.brandLogoUrl}
                  alt={footerContent.brandText || companyName}
                  style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    fontSize: 'clamp(1.5rem, 8vw, 4rem)',
                    fontWeight: 800,
                    letterSpacing: '-0.05em',
                    lineHeight: 0.9,
                    color: sgFooterInk,
                    textTransform: 'lowercase',
                    overflowWrap: 'break-word',
                  }}
                >
                  <Editable as="span" field="footerContent.brandText" value={footerContent.brandText || companyName} />
                </div>
              )}
            </div>
            {sugarColumns.map((col, idx) => (
              <div key={`sugar-col-${idx}`}>
                <h3
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: sgLime,
                    margin: '0 0 18px',
                    textTransform: 'uppercase',
                  }}
                >
                  {col.title}
                </h3>
                <ul className="flex flex-col gap-3">
                  {col.links.map((link, linkIdx) => {
                    const isExternal = /^https?:\/\//i.test(link.url);
                    const linkStyle: React.CSSProperties = {
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      color: sgFooterInk,
                      opacity: 0.92,
                      textTransform: 'lowercase',
                      letterSpacing: '-0.01em',
                    };
                    return (
                      <li key={`sugar-link-${idx}-${linkIdx}`}>
                        {isExternal ? (
                          <a href={link.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                            {link.label}
                          </a>
                        ) : (
                          <Link to={link.url || '#'} style={linkStyle}>{link.label}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 64,
              paddingTop: 24,
              borderTop: `1px solid color-mix(in srgb, ${sgFooterInk} 22%, transparent)`,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              fontSize: '0.78rem',
              fontWeight: 600,
              color: sgFooterInk,
              opacity: 0.78,
              textTransform: 'lowercase',
              letterSpacing: '-0.01em',
            }}
            className="md:flex-row md:items-center md:justify-between"
          >
            <span><Editable as="span" field="footerContent.copyright" value={formatCopyright(footerContent.copyright, companyName, currentYear)} /></span>
            {footerContent.brandTagline ? (
              <span style={{ color: sgSecondary, fontWeight: 800 }}><Editable as="span" field="footerContent.brandTagline" value={footerContent.brandTagline} /></span>
            ) : null}
          </div>
        </div>
      </footer>
    );
  }

  // Shared "Newsletter" footer - newsletter band + multi-column links + bottom strip.
  // Same shape used by Rouge / Luxe / Neon / Bloom / Kinetic; differences are
  // colors/fonts only, all driven by tenant tokens.
  if (isNewsletterFooterVariant) {
    // Suppress the footer's newsletter band when the current page already
    // renders a dedicated `newsletter` section (otherwise two subscribe forms
    // stack back-to-back). Sections come from the live/preview config here.
    const pageSections: any[] = Array.isArray(storefrontConfig?.customSections)
      ? storefrontConfig.customSections
      : Array.isArray(storefrontConfig?.sections)
        ? storefrontConfig.sections
        : [];
    const pageHasNewsletterSection = pageSections.some((s: any) => {
      // Check ALL identifying fields, not the first truthy one — the newsletter
      // section's category is "cta" while its name/slug carry "newsletter".
      const key = (typeof s === 'string'
        ? s
        : [s?.category, s?.type, s?.sectionSlug, s?.slug, s?.name].filter(Boolean).join(' ')
      ).toLowerCase();
      return key.includes('newsletter');
    });
    return <NewsletterFooter hideNewsletterBand={currentPage === 'home' && pageHasNewsletterSection} />;
  }

  return (
    <footer
      className="mt-auto py-12"
      style={{
        backgroundColor: footerBgColor,
        color: footerTextColor
      }}
    >
      <div className="container mx-auto px-4">
        {isSeedFooterVariant ? (
          <>
            <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-center gap-3">
                {logo ? (
                  <img
                    src={logo}
                    alt={companyName}
                    className="h-12 w-auto object-contain"
                  />
                ) : (
                  <>
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl"
                      style={{
                        backgroundColor: primaryColor,
                        color: '#FFFFFF'
                      }}
                    >
                      {logoInitial}
                    </div>
                    <span className="text-xl font-bold" style={{ color: footerTextColor }}>
                      {companyName}
                    </span>
                  </>
                )}
              </div>

              <div className={`grid gap-8 sm:grid-cols-2 ${(footerContent.menuColumns || []).length >= 4 ? 'sm:grid-cols-4' : (footerContent.menuColumns || []).length === 3 ? 'sm:grid-cols-3' : ''} lg:gap-12 text-sm`}>
                {(footerContent.menuColumns || []).map((section, sectionIdx) => (
                  <div key={`footer-layout-section-${sectionIdx}`} className="space-y-3">
                    <p className="text-xs font-semibold tracking-[0.12em] uppercase" style={{ color: footerTextColor, opacity: 0.75 }}>
                      {section.title}
                    </p>
                    <div className="space-y-2">
                      {section.links.map((link, linkIdx) => {
                        const isExternal = /^https?:\/\//i.test(link.url);
                        return (
                          <a
                            key={`footer-layout-link-${sectionIdx}-${linkIdx}`}
                            href={link.url || '#'}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noreferrer' : undefined}
                            className="block hover:opacity-100 transition-opacity"
                            style={{ color: footerTextColor, opacity: 0.9 }}
                          >
                            {link.label}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {footerContent.socials && footerContent.socials.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {footerContent.socials.map((s, idx) => {
                  const label = s.label || s.platform.charAt(0).toUpperCase() + s.platform.slice(1);
                  const isExternal = /^https?:\/\//i.test(s.url);
                  return (
                    <a
                      key={`fs-${idx}`}
                      href={s.url}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      aria-label={label}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity"
                      style={{ border: `1px solid ${footerTextColor}33`, color: footerTextColor, opacity: 0.8 }}
                    >
                      {footerSocialIconFor(s)}
                    </a>
                  );
                })}
              </div>
            )}

            <p className="mt-10 text-sm" style={{ color: footerTextColor, opacity: 0.8 }}>
              <Editable as="span" field="footerContent.copyright" value={formatCopyright(footerContent.copyright, companyName, currentYear)} />
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-6">
            {/* Logo */}
            <div className="flex items-center gap-3">
              {logo ? (
                <img
                  src={logo}
                  alt={companyName}
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <>
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl"
                    style={{ 
                      backgroundColor: primaryColor,
                      color: '#FFFFFF'
                    }}
                  >
                    {logoInitial}
                  </div>
                  <span className="text-xl font-bold" style={{ color: footerTextColor }}>
                    {companyName}
                  </span>
                </>
              )}
            </div>

            {/* Brand bio */}
            {footerContent.brandBio && (
              <p className="text-sm text-center max-w-xl" style={{ color: footerTextColor, opacity: 0.75 }}>
                <Editable as="span" field="footerContent.brandBio" value={footerContent.brandBio} />
              </p>
            )}

            {/* Socials */}
            {footerContent.socials && footerContent.socials.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                {footerContent.socials.map((s, idx) => {
                  const label = s.label || s.platform.charAt(0).toUpperCase() + s.platform.slice(1);
                  const isExternal = /^https?:\/\//i.test(s.url);
                  return (
                    <a
                      key={`fs-${idx}`}
                      href={s.url}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      aria-label={label}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity"
                      style={{ border: `1px solid ${footerTextColor}33`, color: footerTextColor, opacity: 0.8 }}
                    >
                      {footerSocialIconFor(s)}
                    </a>
                  );
                })}
              </div>
            )}

            {/* Bottom Links */}
            <div className="flex items-center gap-2 text-sm flex-wrap justify-center" style={{ color: footerTextColor, opacity: 0.8 }}>
              {(footerContent.bottomLinks && footerContent.bottomLinks.length > 0 ? footerContent.bottomLinks : [
                { label: 'Privacy Policy', url: '/privacy' },
                { label: 'Terms & Conditions', url: '/terms' },
              ]).map((link, idx, arr) => {
                const isExternal = /^https?:\/\//i.test(link.url);
                return (
                  <span key={`bl-${idx}`} className="flex items-center gap-2">
                    {isExternal ? (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.url || '#'} className="hover:opacity-100 transition-opacity">
                        {link.label}
                      </Link>
                    )}
                    {idx < arr.length - 1 && <span style={{ opacity: 0.5 }}>|</span>}
                  </span>
                );
              })}
            </div>

            {/* Copyright */}
            <p className="text-sm" style={{ color: footerTextColor, opacity: 0.8 }}>
              <Editable as="span" field="footerContent.copyright" value={formatCopyright(footerContent.copyright, companyName, currentYear)} />
            </p>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
